/**
 * Filter memory for the four tracker pages: which URL params are remembered on
 * the account and restored when the page is opened without them.
 *
 * Only filters, search, tab and view keys are listed. Paging, sort and the
 * free-text boxes on these pages are component state rather than URL params,
 * so there is nothing of theirs to remember here.
 *
 * Keys deliberately LEFT OUT:
 * - `removed` (/client-deliverables): the Removed view is for restoring a row,
 *   not a place to live (page note on SAVED_FILTER_KEYS).
 * - `nonEmptyOnly`, `groupBy` (/sales-tracker): retired keys the page only
 *   scrubs; nothing reads them.
 */
import type { FilterMemoryRule } from './types';

export const FILTER_MEMORY_TRACKERS: readonly FilterMemoryRule[] = [
  {
    path: '/client-deliverables',
    keep: [
      'deliverables', // tab: `capacity` = Project Capacity; absent = Deliverables
      'view', // `table`; absent = sheet (default)
      'companyId', // filter: company
      'productId', // filter: product
      'projectId', // filter: project
      'intakeId', // filter: intake
      'cycleId', // filter: cycle
      'regionId', // filter: region
      'source', // filter: row source
      'billingStatus', // filter: billing status
      'outstanding', // filter: still to deliver / nothing outstanding
      'capacityCycleId', // Project Capacity tab: cycle
      'capacityProjectId', // Project Capacity tab: projects (comma list)
    ],
  },
  {
    path: '/future-billables-tracker',
    keep: [
      'display', // view: `sheet`; absent = table
      'companyId', // filter: company
      'projectId', // filter: project
      'productId', // filter: product
      'currency', // filter: currency
      'kind', // filter: quote / invoice
      'amountMin', // filter: amount lower bound
      'amountMax', // filter: amount upper bound
      'plannedIn30Days', // filter: planned within 30 days
    ],
  },
  {
    path: '/revenue-tracker',
    keep: [
      'view', // `table`; absent = sheet (default)
      'q', // search: company name
      'region', // filter: billing-region band
      'cycle', // filter: cycle
    ],
  },
  {
    path: '/sales-tracker',
    keep: [
      'view', // `sheet`; absent = table
      'cycleId', // filter: cycle (page seeds the running cycle when absent)
      'projectId', // filter: project lanes (comma list, cycle-scoped)
    ],
  },
];
